import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { components } from '@/api/schema'
import { useMutation, useQuery } from '@/api/query'
import { relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Schedule = components['schemas']['ScheduleOut']
type Condition = components['schemas']['ConditionOut']
type Frequency = Schedule['frequency']
type Scope = Schedule['scope']

/**
 * Run triggers — S-D5's `seo#triggers`, on the real schedule (S-B29).
 *
 * **Three controls, not one "automation level" slider**, because the design's own
 * note says they are three different promises: a schedule is predictable, a manual
 * run is the tenant's own decision, and an automatic run is *"the one that fires
 * while nobody is watching, so it is off by default, every condition names its own
 * threshold"*.
 *
 * **Nothing here knows what a condition means.** The label, the bounds and the
 * default all come off `GET /seo/run-schedules/{id}`, because `app/runs/triggers.CONDITIONS` is
 * where the list and its order are decided — a copy in TypeScript would be the
 * hand-written DTO REQUIREMENTS §9.1 forbids, and it would be the copy that
 * disagreed about which switch exists.
 *
 * WHAT IS DELIBERATELY NOT ON THIS SCREEN
 * -----------------------------------------
 * The design's third card ends with *"Never spend more than this on automatic runs
 * — ₹2,000/month"*. **It is not rendered, and that is a decision rather than an
 * omission.** The ledger that would enforce it is S-B30's; a number stored and not
 * enforced is worse than no field at all, because the tenant reads it as a cap and
 * it is a note. The alert above the switches carries the part that *is* true today
 * — an automatic run cannot publish anything — and S-B30 adds the input beside the
 * ledger that binds it.
 *
 * WHY SAVE CAN SUCCEED AND STILL SHOW A WARNING
 * -----------------------------------------------
 * `PUT /seo/run-schedules/{id}` writes the row, then asks EventBridge Scheduler to agree. The
 * second half can fail on its own, and when it does the response is still a 200
 * with `synced: false` — the tenant's setting *did* save. Reporting that as an
 * error would send them to press Save again, which changes nothing. So a failed
 * sync is a warning on the card with the reason on it, and the operator's repair
 * command is what clears it.
 */
export function RunTriggers(): JSX.Element {
  const toast = useToast()
  const [siteId, setSiteId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const selectedId = siteId ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null

  const schedule = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/run-schedules/{site_id}', {
              params: { path: { site_id: selectedId } },
              signal,
            }),
          ),
    [selectedId],
  )

  const runs = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/runs', { params: { query: { site_id: selectedId } }, signal }),
          ),
    [selectedId],
  )

  if (sites.state.status === 'loading') return <Loading label="Loading your sites" />
  if (sites.state.status === 'error') {
    return <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
  }

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">When we run</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          <select
            className="ar-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {site === null ? (
        <div className="ar-card">
          <div className="ar-card__body">
            <p className="ar-muted">
              Add a site before setting up when AugRank runs. Nothing can be scheduled until there
              is something to run against.
            </p>
          </div>
        </div>
      ) : schedule.state.status === 'loading' ? (
        <Loading label="Loading this site's schedule" />
      ) : schedule.state.status === 'error' ? (
        <ErrorState failure={schedule.state.failure} onRetry={schedule.refetch} />
      ) : schedule.state.data === null ? null : (
        <TriggerCards
          key={site.id}
          domain={site.domain}
          schedule={schedule.state.data}
          activeRunId={runs.state.data?.active?.id ?? null}
          onSaved={(saved) => {
            schedule.refetch()
            toast.show({
              kind: saved.synced ? 'success' : 'info',
              title: 'Saved',
              body: saved.synced
                ? describeCadence(saved)
                : 'Your settings are saved. AugRank could not reach the scheduler — it will be retried.',
            })
          }}
          onTriggered={(started) => {
            runs.refetch()
            toast.show({
              kind: 'success',
              title: started ? 'Run started' : 'A run is already going',
              body: started
                ? `AugRank is working on ${site.domain}. The trail will fill in as it goes.`
                : `AugRank is already running on ${site.domain}. This one will finish first.`,
            })
          }}
        />
      )}
    </>
  )
}

/** The three cards, over one site's schedule. Its own component so that switching
 *  site remounts it and the draft state cannot leak from one site to another. */
function TriggerCards({
  domain,
  schedule,
  activeRunId,
  onSaved,
  onTriggered,
}: {
  domain: string
  schedule: Schedule
  activeRunId: string | null
  onSaved: (saved: Schedule) => void
  onTriggered: (started: boolean) => void
}): JSX.Element {
  const [draft, setDraft] = useState<Schedule>(schedule)

  // The server is the authority: a save returns the row as stored — thresholds
  // clamped, conditions normalised — and the form has to show what was kept
  // rather than what was typed.
  useEffect(() => {
    setDraft(schedule)
  }, [schedule])

  const save = useMutation((body: Schedule) =>
    call(() =>
      api.PUT('/api/v1/seo/run-schedules/{site_id}', {
        params: { path: { site_id: body.site_id } },
        body: {
          enabled: body.enabled,
          frequency: body.frequency,
          hour: body.hour,
          minute: body.minute,
          // `?? null` rather than passing it through: the response type has the
          // column optional, the request type has it nullable-but-required, and
          // omitting it would let a schedule switched away from weekly keep a day.
          weekday: body.weekday ?? null,
          timezone: body.timezone,
          scope: body.scope,
          conditions: Object.fromEntries(
            body.conditions.map((condition) => [
              condition.name,
              { enabled: condition.enabled, threshold: condition.threshold },
            ]),
          ),
        },
      }),
    ),
  )

  const trigger = useMutation((scope: Scope) =>
    call(() =>
      api.POST('/api/v1/seo/sites/{site_id}/runs', {
        params: { path: { site_id: schedule.site_id } },
        body: { scope },
      }),
    ),
  )

  const dirty = JSON.stringify(draft) !== JSON.stringify(schedule)
  const enabledCount = draft.conditions.filter((condition) => condition.enabled).length
  // The *saved* schedule's prediction, not the draft's: an unsaved change to the
  // time has not moved when it will actually fire, and showing the draft's would
  // tell the tenant a run was coming that nothing has been told about.
  const nextRunAt = schedule.next_run_at ?? null

  return (
    <div className="ar-stack ar-stack--loose" style={{ maxWidth: '46rem' }}>
      {/* --- on a schedule ------------------------------------------------- */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>On a schedule</h3>
          <span className="ar-card__spacer" />
          <label className="ar-switch">
            <input
              type="checkbox"
              aria-label="Run on a schedule"
              checked={draft.enabled}
              onChange={(event) => {
                setDraft({ ...draft, enabled: event.target.checked })
              }}
            />
            <span className="ar-switch__track" aria-hidden="true" />
          </label>
        </div>
        <div className="ar-card__body ar-stack">
          {draft.enabled && !schedule.synced && schedule.sync_error !== null ? (
            <div className="ar-alert ar-alert--warning" role="status">
              <span className="ar-alert__icon" aria-hidden="true">
                !
              </span>
              <div className="ar-alert__body">
                <strong>This schedule is saved but not live yet</strong>
                AugRank could not register it with the scheduler, so it will not fire until that is
                retried. {schedule.sync_error}
              </div>
            </div>
          ) : null}

          <div className="ar-grid ar-grid--2">
            <Field label="How often">
              {(props) => (
                <select
                  {...props}
                  className="ar-select"
                  value={draft.frequency}
                  onChange={(event) => {
                    const frequency = event.target.value as Frequency
                    setDraft({
                      ...draft,
                      frequency,
                      // A weekday is required for `weekly` and forbidden otherwise —
                      // `ck_run_schedules_weekly_has_weekday`, and `Schedule.validate`
                      // refuses the mismatch before anything is written. Monday is the
                      // default because it is the day the design's own copy shows.
                      weekday: frequency === 'weekly' ? (draft.weekday ?? 1) : null,
                    })
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekdays">Weekdays only</option>
                  <option value="weekly">Weekly</option>
                </select>
              )}
            </Field>

            <Field
              label="At"
              help={`${draft.timezone.replace('_', ' ')} — your organisation's time zone, never the server's.`}
            >
              {(props) => (
                <select
                  {...props}
                  className="ar-select"
                  value={`${pad(draft.hour)}:${pad(draft.minute)}`}
                  onChange={(event) => {
                    const [hour, minute] = event.target.value.split(':')
                    setDraft({ ...draft, hour: Number(hour), minute: Number(minute) })
                  }}
                >
                  {TIMES.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          {draft.frequency === 'weekly' ? (
            <Field label="On">
              {(props) => (
                <select
                  {...props}
                  className="ar-select"
                  value={String(draft.weekday ?? 1)}
                  onChange={(event) => {
                    setDraft({ ...draft, weekday: Number(event.target.value) })
                  }}
                >
                  {WEEKDAYS.map((day, index) => (
                    <option key={day} value={index + 1}>
                      {day}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : null}

          <Field
            label="What a scheduled run does"
            help="Drafting is the expensive half. A run that only checks ranks costs about ₹6; a full run costs about ₹18."
          >
            {(props) => (
              <select
                {...props}
                className="ar-select"
                value={draft.scope}
                onChange={(event) => {
                  setDraft({ ...draft, scope: event.target.value as Scope })
                }}
              >
                <option value="full">Audit, rank check, and draft content</option>
                <option value="audit_rank">Audit and rank check only</option>
                <option value="rank_only">Rank check only — cheapest</option>
              </select>
            )}
          </Field>

          {schedule.enabled && nextRunAt !== null ? (
            <p className="ar-subtle" style={{ margin: 0 }}>
              Next run {relativeTime(nextRunAt, new Date())}. Predicted — the scheduler is the
              authority.
            </p>
          ) : null}
        </div>
      </section>

      {/* --- by hand ------------------------------------------------------- */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>By hand</h3>
          <span className="ar-card__spacer" />
          <span className="ar-badge ar-badge--success">✓ Always available</span>
        </div>
        <div className="ar-card__body">
          <p className="ar-muted" style={{ marginTop: 0 }}>
            A run you start yourself cannot be turned off — a tenant who has just changed {domain}{' '}
            should never have to wait for tomorrow&rsquo;s schedule to see whether it worked.
          </p>
          {trigger.state.status === 'error' ? (
            <InlineError failure={trigger.state.failure} />
          ) : null}
          <div className="ar-row">
            <Button
              tone="ghost"
              disabled={activeRunId !== null || trigger.pending}
              onClick={() => {
                void trigger.run(draft.scope).then((result) => {
                  if (!result.ok) return
                  onTriggered(result.data.started)
                })
              }}
            >
              {activeRunId !== null ? 'A run is going…' : 'Run a full pass now'}
            </Button>
            <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
              Runs {describeScope(draft.scope)}
            </span>
          </div>
        </div>
      </section>

      {/* --- automatically -------------------------------------------------- */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Automatically, when something changes</h3>
          <span className="ar-card__spacer" />
          <span className="ar-badge ar-badge--neutral">
            {enabledCount} of {draft.conditions.length} on
          </span>
        </div>
        <div className="ar-card__body ar-stack">
          <div className="ar-alert ar-alert--info">
            <span className="ar-alert__icon" aria-hidden="true">
              ◷
            </span>
            <div className="ar-alert__body">
              <strong>An automatic run still cannot publish anything</strong>
              It produces drafts and technical fixes, and whatever you gated stays gated. The two
              settings are independent on purpose.
            </div>
          </div>

          {draft.conditions.map((condition) => (
            <ConditionRow
              key={condition.name}
              condition={condition}
              onChange={(next) => {
                setDraft({
                  ...draft,
                  conditions: draft.conditions.map((row) => (row.name === next.name ? next : row)),
                })
              }}
            />
          ))}
        </div>
        <div className="ar-card__footer">
          {save.state.status === 'error' ? <InlineError failure={save.state.failure} /> : null}
          <span className="ar-card__spacer" />
          <Button
            disabled={!dirty || save.pending}
            onClick={() => {
              void save.run(draft).then((result) => {
                if (!result.ok) return
                onSaved(result.data)
              })
            }}
          >
            {save.pending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </section>
    </div>
  )
}

/** One automatic trigger: its switch, and the threshold it fires at. */
function ConditionRow({
  condition,
  onChange,
}: {
  condition: Condition
  onChange: (next: Condition) => void
}): JSX.Element {
  return (
    <div className="app-gate__row">
      <label className="ar-switch">
        <input
          type="checkbox"
          aria-label={condition.label}
          checked={condition.enabled}
          onChange={(event) => {
            onChange({ ...condition, enabled: event.target.checked })
          }}
        />
        <span className="ar-switch__track" aria-hidden="true" />
      </label>
      <div style={{ flex: 1 }}>
        <strong>{condition.label}</strong>
        <div className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          Runs {describeScope(condition.scope as Scope)}.
        </div>
        {condition.enabled ? (
          <div className="ar-row" style={{ marginTop: 'var(--ar-space-2)' }}>
            <label className="ar-field__label" htmlFor={`threshold-${condition.name}`}>
              Threshold
            </label>
            <input
              id={`threshold-${condition.name}`}
              className="ar-input ar-num"
              type="number"
              style={{ maxWidth: '6rem' }}
              min={condition.minimum}
              max={condition.maximum}
              value={condition.threshold}
              onChange={(event) => {
                // Not clamped here. The server clamps on write and returns what it
                // stored, so a typed 0 comes back as the floor rather than being
                // silently corrected under the tenant's cursor mid-keystroke.
                onChange({ ...condition, threshold: Number(event.target.value) })
              }}
            />
            <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
              between {condition.minimum} and {condition.maximum}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** The times the design offers. A closed list rather than a time input, because
 *  `app/runs/cron.py` has no free-text cron field and this is the other end of
 *  that decision — see its docstring on what a tenant-typed cron would cost. */
const TIMES = ['02:00', '04:00', '06:00', '22:00'] as const

const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function describeScope(scope: Scope): string {
  if (scope === 'rank_only') return 'a rank check only'
  if (scope === 'audit_rank') return 'an audit and a rank check'
  return 'an audit, a rank check and drafts content'
}

function describeCadence(schedule: Schedule): string {
  if (!schedule.enabled) return 'AugRank will not run on its own for this site.'
  const at = `${pad(schedule.hour)}:${pad(schedule.minute)}`
  if (schedule.frequency === 'daily') return `AugRank will run every day at ${at}.`
  if (schedule.frequency === 'weekdays') return `AugRank will run on weekdays at ${at}.`
  const day = WEEKDAYS[(schedule.weekday ?? 1) - 1] ?? 'Monday'
  return `AugRank will run every ${day} at ${at}.`
}
